<script setup>
import { defineProps, ref, onMounted, nextTick, watch } from "vue";
import { AgGridVue } from "ag-grid-vue3";
import { AllCommunityModule, ModuleRegistry, themeQuartz, PaginationModule, ColumnAutoSizeModule,  QuickFilterModule } from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule, PaginationModule, ColumnAutoSizeModule, QuickFilterModule]);

const props = defineProps({
    rowData: {
        type: Array,
        required: true,
        validator: (value) => {
            return Array.isArray(value) && value.every(item => typeof item === 'object');
        }
    },
        columnDefs: {
        type: Array,
        required: true,
        validator: (value) => {
            return Array.isArray(value) && value.every(col => {
                return col.headerName && col.field && 
                       (!col.type || ['link', 'multiLink', 'text', 'date', 'status', 'currency'].includes(col.type)) &&
                       (!col.type || col.type !== 'link' || typeof col.getLink === 'function');
            });
        }
    },
    quickFilterText: {
        type: String,
        required: false,
        default: ''
    },
    useFitGridWidth: {
        type: Boolean,
        default: true
    },
    rowHeight: {
        type: Number,
        default: 80
    },
    tableHeight: {
        type: Number,
        default: 450
    }
});

const gridApi = ref(null);
const columnApi = ref(null);

const theme = themeQuartz.withParams(
    {
        headerTextColor: 'rgb(255 255 255)',
        headerBackgroundColor: "rgb(23 23 23)",
        backgroundColor: "rgb(0 0 0)",    
        foregroundColor: "rgb(255 255 255)",
        accentColor: "rgb(255 255 255)",
        fontFamily: "Inter, sans-serif",
        browserColorScheme: "dark",
    }
);

const formatDate = (utcDate) => {
    if (!utcDate) return '';
    const date = new Date(utcDate);
    
    const dateStr = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });

    const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'
    });

    return `${dateStr} at ${timeStr} ET`;
};

function formatCurrency(price) {

  if (Number.isInteger(price)) {
    return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const getStatusStyle = (type) => {
    const styles = {
        'success': 'font-medium bg-badge-green/20 text-badge-green border border-badge-green',
        'warning': 'font-medium bg-badge-yellow/20 text-badge-yellow border border-badge-yellow',
        'error': 'font-medium bg-badge-red/20 text-badge-red border border-badge-red',
        'info': 'font-medium bg-badge-blue/20 text-badge-blue border border-badge-blue',
    };
    return styles[type] || 'bg-gray-900 text-gray-300';
};

const multiLinkCellRenderer = (params) => {
    const container = document.createElement('div');
    container.className = 'flex flex-col justify-center w-full overflow-hidden';

    params.value.links.forEach((link, index) => {
        const linkContainer = document.createElement('div');
        linkContainer.className = 'flex flex-col gap-0 w-full overflow-hidden';

        const anchor = document.createElement('a');
        anchor.href = link.href;
        anchor.target = '_blank';
        anchor.innerHTML = link.text;
        anchor.className = 'text-gray-100 hover:text-echoes-primary w-full overflow-hidden text-ellipsis whitespace-nowrap';
        linkContainer.appendChild(anchor);

        if (link.subtitle) {
            const subtitle = document.createElement('span');
            subtitle.innerHTML = link.subtitle;
            subtitle.className = 'text-sm text-gray-500 -mt-2.5 w-full overflow-hidden text-ellipsis whitespace-nowrap';
            linkContainer.appendChild(subtitle);
        }
        
        container.appendChild(linkContainer);
        
        if (index < params.value.links.length - 1) {
            const separator = document.createElement('span');
            separator.innerHTML = ' - ';
            separator.className = 'text-gray-500';
            container.appendChild(separator);
        }
    });

    return container;
};

const textCellRenderer = (params) => {
    const div = document.createElement('div');
    div.innerHTML = params.value;
    div.className = `w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-md flex items-center gap-1.5 ${params.value?.className}`;
    return div;
};

const copyableCellRenderer = (params) => {
    const container = document.createElement('div');
    container.className = 'flex items-center gap-2 cursor-pointer hover:text-echoes-primary group';
    
    const textSpan = document.createElement('span');
    textSpan.textContent = params.value;
    
    const copyIcon = document.createElement('svg');
    copyIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 opacity-0 group-hover:opacity-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>`;
    
    container.appendChild(textSpan);
    container.appendChild(copyIcon);
    
    container.onclick = async () => {
        try {
            await navigator.clipboard.writeText(params.data.payment_tooltip);
            const originalText = textSpan.textContent;
            const originalClass = textSpan.className;
            
            textSpan.textContent = 'Copied!';
            textSpan.className = 'text-badge-green';
            copyIcon.style.display = 'none';
            
            setTimeout(() => {
                textSpan.textContent = originalText;
                textSpan.className = originalClass;
                copyIcon.style.display = 'block';
            }, 1000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };
    
    return container;
};

const statusCellRenderer = (params) => {
    const badgeConfig = params.value;
    if (!badgeConfig || !badgeConfig.text || !badgeConfig.type) return '';
    
    const div = document.createElement('div');
    const statusClass = getStatusStyle(badgeConfig.type);
    div.className = `w-fit px-4 py-1 text-xs my-2 rounded-full ${statusClass}`;
    div.innerHTML = badgeConfig.text;
    return div;
};

const actionCellRenderer = (params) => {
   
    const div = document.createElement('div');
    div.innerHTML = params.value.innerHTML;
    div.className = `w-fit font-semibold self-center text-xs rounded-md flex items-center gap-1.5 ${params.value.className}`;
    div.onclick = params.value.onClick;
    return div;
};

const autoSizeStrategy = ref({
    type: 'fitGridWidth',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    defaultMinWidth: 100,
    columnLimits: [
        {
            colId: 'action',
            minWidth: 120,
            maxWidth: 150,
            suppressSizeToFit: true
        },
        {
            colId: 'status',
            minWidth: 100,
            maxWidth: 160
        },
        {
            colId: 'amount',
            minWidth: 120,
            maxWidth: 220
        },
        {
            colId: 'seller_link',
            minWidth: 120,
            maxWidth: 300
        },
        {
            colId: 'expiration_date',
            minWidth: 140,
            maxWidth: 280
        },
        {
            colId: 'created_at',
            minWidth: 160,
            maxWidth: 280
        },
        {
            colId: 'listing',
            minWidth: 300,
            maxWidth: 800
        },
        {
            colId: 'buyer',
            minWidth: 150,
            maxWidth: 300
        },
        {
            colId: 'payment_method',
            minWidth: 120,
            maxWidth: 200
        }
    ]
});

const dateCellRenderer = (params) => {
    if (!params.value) return '';
    const div = document.createElement('div');
    div.className = 'flex flex-col h-full justify-center w-full overflow-hidden';
    
    const formattedDate = formatDate(params.data[params.column.colId]);
    const [date, time] = formattedDate.split(' at ');
    
    const dateSpan = document.createElement('span');
    dateSpan.textContent = date;
    dateSpan.className = 'text-gray-100 leading-tight w-full overflow-hidden text-ellipsis whitespace-nowrap';
    
    const timeSpan = document.createElement('span');
    timeSpan.textContent = time;
    timeSpan.className = 'text-gray-400 text-sm leading-tight w-full overflow-hidden text-ellipsis whitespace-nowrap';
    
    div.appendChild(dateSpan);
    div.appendChild(timeSpan);
    return div;
};

const tagCellRenderer = (params) => {
    const div = document.createElement('div');
    div.className = 'w-fit self-center rounded-full text-sm font-semibold flex items-center gap-1 py-1 px-3';
    
    if (params.data.display_icon) {
        const icon = document.createElement('i');
        icon.className = `${params.data.display_icon}`;
        div.appendChild(icon);
    }

    const textSpan = document.createElement('span');
    textSpan.textContent = params.value;
    div.appendChild(textSpan);
    
    div.style.backgroundColor = params.data.display_color;
    div.style.color = '#000';

    if (!params.data.display_color) {
        div.style.backgroundColor = '#fff';
        div.style.color = '#000';
    }
    
    return div;
};

const processedColumnDefs = props.columnDefs.map(col => {
    const baseConfig = {
        ...col,
        resizable: true,
        suppressSizeToFit: false,
        autoHeight: false,
        wrapText: false,
        cellStyle: {
            paddingRight: 0,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
        },
        headerStyle: {
            paddingRight: 0,
            fontWeight: 'semibold',
            textTransform: 'uppercase',
        }
    };

    if (col.field === 'action') {
        return {
            ...baseConfig,
            cellRenderer: actionCellRenderer,
            cellStyle: {
                ...baseConfig.cellStyle,
                justifyContent: 'start',
            },
            minWidth: 120,
            maxWidth: 150,
            suppressSizeToFit: true,
            resizable: false,
            flex: 0,
            width: 150
        };
    }
    
    if (col.type === 'multiLink') {
        return {
            ...baseConfig,
            cellRenderer: multiLinkCellRenderer,
            getQuickFilterText: (params) => {
                if (!params.value?.links) return '';
                return params.value.links.map(link => link.text).join(' ');
            },
            flex: col.flex || 1,
            minWidth: col.minWidth || 150,
            maxWidth: col.maxWidth || 300,
            suppressSizeToFit: false
        };
    }

    if (col.type === 'date') {
        return {
            ...baseConfig,
            cellRenderer: dateCellRenderer,
            getQuickFilterText: (params) => formatDate(params.value) || '',
            flex: col.flex || 1,
            minWidth: col.minWidth || 140,
            maxWidth: col.maxWidth || 200,
            suppressSizeToFit: false
        };
    }

    if (col.type === 'currency') {
        return {
            ...baseConfig,
            valueFormatter: (params) => formatCurrency(params.value),
            getQuickFilterText: (params) => params.value?.toString() || '',
            flex: col.flex || 1,
            minWidth: col.minWidth || 100,
            maxWidth: col.maxWidth || 150,
            suppressSizeToFit: false
        };
    }

    if (col.type === 'status') {
        return {
            ...baseConfig,
            cellRenderer: statusCellRenderer,
            getQuickFilterText: (params) => params.value?.text || '',
            flex: col.flex || 1,
            minWidth: col.minWidth || 100,
            maxWidth: col.maxWidth || 120,
            suppressSizeToFit: false
        };
    }
    
    if (col.type === 'text') {
        if (col.field === 'payment') {
            return {
                ...baseConfig,
                cellRenderer: copyableCellRenderer,
                getQuickFilterText: (params) => params.value?.toString() || '',
                flex: col.flex || 1,
                minWidth: col.minWidth || 120,
                maxWidth: col.maxWidth || 200,
                suppressSizeToFit: false
            };
        }
        return {
            ...baseConfig,
            cellRenderer: textCellRenderer,
            getQuickFilterText: (params) => params.value?.toString() || '',
            flex: col.flex || 1,
            minWidth: col.minWidth || 100,
            maxWidth: col.maxWidth || 200,
            suppressSizeToFit: false
        };
    }
    
    if (col.type === 'tag') {
        return {
            ...baseConfig,
            cellRenderer: tagCellRenderer,
            getQuickFilterText: (params) => params.value?.toString() || '',
            flex: col.flex || 1,
            minWidth: col.minWidth || 100,
            maxWidth: col.maxWidth || 150,
            suppressSizeToFit: false
        }
    }
    
    // Default column configuration
    return {
        ...baseConfig,
        getQuickFilterText: (params) => params.value?.toString() || '',
        flex: col.flex || 1,
        minWidth: col.minWidth || 100,
        maxWidth: col.maxWidth || 200,
        suppressSizeToFit: false
    };
});

const gridOptions = {
    enableCellTextSelection: true,
    defaultColDef: {
        resizable: true,
        sortable: true,
        sortingOrder: ['desc', 'asc', null],
        autoHeight: false,
        wrapText: false,
        cellStyle: {
            paddingRight: 0,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
        },
        cellClass: 'ag-cell-text-truncate',
        suppressSizeToFit: false
    },
    onGridReady: (params) => {
        gridApi.value = params.api;
        columnApi.value = params.columnApi;
        
        nextTick(() => {
            if (columnApi.value) {
                columnApi.value.autoSizeAllColumns();
            }
        });
    },
    onFirstDataRendered: () => {
        if (columnApi.value) {
            columnApi.value.autoSizeAllColumns();
        }
    }
};

watch(() => props.rowData, () => {
    nextTick(() => {
        if (columnApi.value && gridApi.value) {
            columnApi.value.autoSizeAllColumns();
        }
    });
}, { deep: true });

watch(() => props.columnDefs, () => {
    nextTick(() => {
        if (columnApi.value && gridApi.value) {
            columnApi.value.autoSizeAllColumns();
        }
    });
}, { deep: true });

onMounted(() => {
    const handleResize = () => {
        if (columnApi.value && gridApi.value) {
            columnApi.value.autoSizeAllColumns();
        }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
        window.removeEventListener('resize', handleResize);
    };
});

</script>

<template>
    <div class="w-full">
        <ag-grid-vue
            :autoSizeStrategy="autoSizeStrategy"
            :theme="theme" 
            :rowData="rowData"
            :columnDefs="processedColumnDefs" 
            v-bind="gridOptions"
            :style="{ height: props.tableHeight + 'px' }"
            :pagination="true"
            :rowHeight="props.rowHeight"
            :quickFilterText="props.quickFilterText"
            :sort="desc"
            :tooltipShowDelay="500"
          
            >
        </ag-grid-vue>
    </div>
</template>
<style lang="sass">
    .ag-cell-text-truncate {
      @apply truncate text-ellipsis;
    }
    
    .ag-grid-vue {
        @apply w-full;
    }
    
    .ag-header-cell {
        @apply overflow-hidden;
    }
    
    .ag-cell {
        @apply overflow-hidden;
    }
    
    .ag-cell {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
    }
    
    .ag-cell a {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: 100% !important;
        display: block !important;
    }
    
    .ag-cell .flex {
        overflow: hidden !important;
    }
    
    .ag-cell .flex a {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: 100% !important;
    }
    
    .ag-cell .flex.flex-col {
        overflow: hidden !important;
    }
    
    .ag-cell .flex.flex-col span {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: 100% !important;
    }
    
    .ag-cell .w-fit {
        overflow: hidden !important;
    }
    
    .ag-cell button {
        overflow: hidden !important;
    }
    
    .ag-cell[title] {
        cursor: help;
    }
    
    .ag-cell * {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
    }
</style>